import React, { useCallback, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';

type Cell = 'X' | 'O' | null;

const LINES = [
  [0, 1, 2],
  [3, 4, 5],
  [6, 7, 8],
  [0, 3, 6],
  [1, 4, 7],
  [2, 5, 8],
  [0, 4, 8],
  [2, 4, 6],
];

function winner(board: Cell[]): Cell {
  for (const [a, b, c] of LINES) {
    if (board[a] != null && board[a] === board[b] && board[a] === board[c]) {
      return board[a];
    }
  }
  return null;
}

// Pick a move for O: win if possible, else block X, else take center/a corner/anything. Good enough
// to be a satisfying opponent without being unbeatable.
function computerMove(board: Cell[]): number {
  const empties = board.map((c, i) => (c == null ? i : -1)).filter((i) => i >= 0);
  const tryComplete = (mark: Cell): number | null => {
    for (const i of empties) {
      const next = board.slice();
      next[i] = mark;
      if (winner(next) === mark) {
        return i;
      }
    }
    return null;
  };
  const win = tryComplete('O');
  if (win != null) return win;
  const block = tryComplete('X');
  if (block != null) return block;
  if (board[4] == null) return 4;
  const corners = [0, 2, 6, 8].filter((i) => board[i] == null);
  if (corners.length > 0) return corners[Math.floor(Math.random() * corners.length)];
  return empties[Math.floor(Math.random() * empties.length)];
}

// You are X, the computer is O. A quick round of noughts-and-crosses while the engine chews.
export default function TicTacToe(): React.ReactNode {
  const [board, setBoard] = useState<Cell[]>(() => Array(9).fill(null));
  const [busy, setBusy] = useState(false);

  const win = useMemo(() => winner(board), [board]);
  const full = board.every((c) => c != null);
  const over = win != null || full;

  const status = win === 'X' ? 'You win! 🎉' : win === 'O' ? 'Computer wins' : full ? 'Draw' : 'Your move (X)';

  const reset = useCallback(() => {
    setBoard(Array(9).fill(null));
    setBusy(false);
  }, []);

  const play = (i: number) => {
    if (busy || over || board[i] != null) {
      return;
    }
    const afterYou = board.slice();
    afterYou[i] = 'X';
    setBoard(afterYou);
    if (winner(afterYou) != null || afterYou.every((c) => c != null)) {
      return;
    }
    // Let your move paint before the computer answers.
    setBusy(true);
    window.setTimeout(() => {
      const afterCpu = afterYou.slice();
      afterCpu[computerMove(afterYou)] = 'O';
      setBoard(afterCpu);
      setBusy(false);
    }, 350);
  };

  return (
    <div className="cm-mini">
      <div className={`cm-mini-status${win === 'X' ? ' cm-mini-status--win' : ''}`}>{status}</div>
      <div className="ttt-board">
        {board.map((c, i) => (
          <button
            key={i}
            type="button"
            className={`ttt-cell${c ? ` ttt-cell--${c.toLowerCase()}` : ''}`}
            onClick={() => play(i)}
            disabled={c != null || over || busy}
            aria-label={c ? `Cell ${i + 1}, ${c}` : `Play cell ${i + 1}`}
          >
            {c}
          </button>
        ))}
      </div>
      <div className="cm-mini-actions">
        <Button variant="secondary" size="sm" onClick={reset}>
          New game
        </Button>
      </div>
    </div>
  );
}
